import { downloadFile, fetchCsvExport } from '@/lib/utils/exports'
import { extractZodIssues } from '@/lib/utils/zodErrorHelpers'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  INITIATIVE_LIST_TABS,
  isClrErrorType,
  isInitiativeErrorType,
  type InitiativeChainDecision,
  type InitiativeListTab,
  type RclMultiplierPaymentTerm,
  type RiskClassCode,
} from '@repo/constants'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export { INITIATIVE_LIST_TABS }

export const initiativesSearchSchema = z.object({
  tab: z.enum(INITIATIVE_LIST_TABS).optional(),
  search: z.string().optional(),
})
export type InitiativesSearch = z.infer<typeof initiativesSearchSchema>

async function listInitiatives(
  tab: InitiativeListTab,
  search: string | undefined,
  signal?: AbortSignal,
) {
  const res = await getApi().credit.initiatives.$get(
    { query: stringifyValues({ tab, search: search || undefined }) },
    { init: { signal } },
  )
  return await res.json()
}

export type InitiativeRow = Awaited<ReturnType<typeof listInitiatives>>['rows'][number]

export function useInitiatives(tab: InitiativeListTab, search: string | undefined) {
  return useQuery({
    queryKey: ['initiatives', tab, search ?? ''],
    queryFn: ({ signal }) => listInitiatives(tab, search, signal),
    placeholderData: keepPreviousData,
  })
}

async function getInitiative(id: number, signal?: AbortSignal) {
  const res = await getApi().credit.initiatives[':id'].$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )
  return await res.json()
}

export type InitiativeDetail = Awaited<ReturnType<typeof getInitiative>>

export function useInitiative(id: number, enabled = true) {
  return useQuery({
    queryKey: ['initiative', id],
    queryFn: ({ signal }) => getInitiative(id, signal),
    enabled,
  })
}

/** DOA preparer slot (or its delegate). Gates the "New initiative" button up front. */
export function useInitiativePreparerEligibility() {
  return useQuery({
    queryKey: ['initiativePreparerEligibility'],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit.initiatives.eligibility.$get(undefined, {
        init: { signal },
      })
      return await res.json()
    },
  })
}

/** Always resolves 200 with a union: `ok: false` is a modal state, never a toast. */
export function useParseInitiativeCsv() {
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await getApi().credit.initiatives.parse.$post({ form: { file } })
      return await res.json()
    },
  })
}

export type InitiativeParseResponse = Awaited<
  ReturnType<ReturnType<typeof useParseInitiativeCsv>['mutateAsync']>
>

function useInitiativeErrorToast() {
  const { t } = useTranslation('credit')
  return (error: unknown) => {
    const issues = extractZodIssues(error)
    const type = (
      issues?.find((issue) => issue.code === 'custom')?.params as { type?: unknown } | undefined
    )?.type
    // The chain endpoints reuse the shared CLR state machine, whose errors (e.g.
    // clrCommentRequired) already carry translations under `clr.errors.*`.
    toast.error(
      isInitiativeErrorType(type)
        ? t(`initiatives.errors.${type}`)
        : isClrErrorType(type)
          ? t(`clr.errors.${type}`)
          : t('initiatives.toasts.error'),
    )
  }
}

function useInvalidateInitiatives() {
  const queryClient = useQueryClient()
  return (id?: number) => {
    queryClient.invalidateQueries({ queryKey: ['initiatives'] })
    if (id !== undefined) queryClient.invalidateQueries({ queryKey: ['initiative', id] })
  }
}

/** Everything the worksheet shows for one initiative — flow actions invalidate all of it. */
function useInvalidateInitiativeWorkspace() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateInitiatives()
  return (id: number) => {
    invalidate(id)
    queryClient.invalidateQueries({ queryKey: ['initiativePayerRows', id] })
    queryClient.invalidateQueries({ queryKey: ['initiativePayerDetail', id] })
    queryClient.invalidateQueries({ queryKey: ['initiativeUplift', id] })
    queryClient.invalidateQueries({ queryKey: ['initiativeVersionImpact', id] })
    queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
  }
}

export function useCreateInitiative() {
  const invalidate = useInvalidateInitiatives()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (input: { name: string; startDate: string; endDate: string; file: File }) => {
      const res = await getApi().credit.initiatives.$post({ form: input })
      return await res.json()
    },
    onSuccess: (created) => {
      invalidate(created.id)
      toast.success(t('initiatives.toasts.created'))
    },
    onError,
  })
}

export function useUpdateInitiative() {
  const invalidate = useInvalidateInitiatives()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (input: {
      id: number
      patch: { name?: string; startDate?: string; endDate?: string }
    }) => {
      const res = await getApi().credit.initiatives[':id'].$patch({
        param: { id: String(input.id) },
        json: input.patch,
      })
      return await res.json()
    },
    onSuccess: (updated) => {
      invalidate(updated.id)
      toast.success(t('initiatives.toasts.updated'))
    },
    onError,
  })
}

export function useDeleteInitiative() {
  const invalidate = useInvalidateInitiatives()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (id: number) => {
      await getApi().credit.initiatives[':id'].$delete({ param: { id: String(id) } })
    },
    onSuccess: (_, id) => {
      invalidate(id)
      toast.warning(t('initiatives.toasts.deleted'))
    },
    onError,
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Worksheet (FUZE-632)
// ─────────────────────────────────────────────────────────────────────────────────────────────

async function listInitiativePayerRows(id: number, signal?: AbortSignal) {
  const res = await getApi().credit.initiatives[':id'].payers.$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )
  return await res.json()
}

export type InitiativePayerRowsResponse = Awaited<ReturnType<typeof listInitiativePayerRows>>
export type InitiativePayerRow = InitiativePayerRowsResponse['rows'][number]

/** ALL payer aggregates in one response — filtered and paginated client-side (mass CLR posture),
 *  which is what keeps bulk-selection semantics sane. */
export function useInitiativePayerRows(id: number, enabled = true) {
  return useQuery({
    queryKey: ['initiativePayerRows', id],
    queryFn: ({ signal }) => listInitiativePayerRows(id, signal),
    enabled,
    placeholderData: keepPreviousData,
  })
}

async function getInitiativePayerDetail(id: number, payerErp: string, signal?: AbortSignal) {
  const res = await getApi().credit.initiatives[':id'].payers[':payerErp'].rows.$get(
    { param: { id: String(id), payerErp } },
    { init: { signal } },
  )
  return await res.json()
}

export type InitiativePayerDetail = Awaited<ReturnType<typeof getInitiativePayerDetail>>
export type InitiativeSoldToRow = InitiativePayerDetail['rows'][number]

/** One payer's sold-to rows — fetched on demand while the details dialog is open. */
export function useInitiativePayerDetail(id: number, payerErp: string | null) {
  return useQuery({
    queryKey: ['initiativePayerDetail', id, payerErp ?? ''],
    queryFn: ({ signal }) => getInitiativePayerDetail(id, payerErp ?? '', signal),
    enabled: payerErp !== null,
  })
}

async function getInitiativeUplift(id: number, signal?: AbortSignal) {
  const res = await getApi().credit.initiatives[':id'].uplift.$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )
  return await res.json()
}

export type InitiativeUpliftResponse = Awaited<ReturnType<typeof getInitiativeUplift>>
export type InitiativeUpliftRow = InitiativeUpliftResponse['rows'][number]

/** The Uplift Calculation tab — every payer of the latest version, RCL + injection + buffer, computed live. */
export function useInitiativeUplift(id: number, enabled = true) {
  return useQuery({
    queryKey: ['initiativeUplift', id],
    queryFn: ({ signal }) => getInitiativeUplift(id, signal),
    enabled,
    placeholderData: keepPreviousData,
  })
}

/** Stored per-risk-class buffer overrides; missing classes render at the 30% default. */
export function useInitiativeBuffers(id: number, enabled = true) {
  return useQuery({
    queryKey: ['initiativeBuffers', id],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit.initiatives[':id'].buffers.$get(
        { param: { id: String(id) } },
        { init: { signal } },
      )
      return await res.json()
    },
    enabled,
  })
}

export type InitiativeBufferEntry = InitiativeUpliftBuffersInput['buffers'][number]
type InitiativeUpliftBuffersInput = {
  buffers: { riskClass: RiskClassCode; buffer: number }[]
}

export function useSaveInitiativeBuffers(id: number) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (input: InitiativeUpliftBuffersInput) => {
      const res = await getApi().credit.initiatives[':id'].buffers.$put({
        param: { id: String(id) },
        json: input,
      })
      return await res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiativeBuffers', id] })
      queryClient.invalidateQueries({ queryKey: ['initiativeUplift', id] })
      toast.success(t('initiatives.uplift.buffers.saved'))
    },
    onError,
  })
}

export type InitiativeRowsPatch =
  | { kind: 'setAmount'; amount: number }
  | { kind: 'resetToFile' }
  | { kind: 'revertToRecommendation' }
  | { kind: 'setUserComment'; comment: string | null }
  | { kind: 'setReviewed'; isReviewed: boolean }
  | { kind: 'setInjectionDecision'; isInjected: boolean | null; overrideComment?: string }
  | { kind: 'setPaymentTerm'; paymentTerm: RclMultiplierPaymentTerm }

/** The autosave path: silent on success (blur-saves and bulk edits fire constantly), toast on error. */
export function usePatchInitiativeRows(id: number) {
  const invalidateWorkspace = useInvalidateInitiativeWorkspace()
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (input: { rowIds: number[]; patch: InitiativeRowsPatch }) => {
      const res = await getApi().credit.initiatives[':id'].rows.$patch({
        param: { id: String(id) },
        json: input,
      })
      return await res.json()
    },
    onSuccess: () => invalidateWorkspace(id),
    onError,
  })
}

export type InitiativeFlowAction =
  | { action: 'send_for_review' }
  | { action: 'send_final' }
  | { action: InitiativeChainDecision; comment: string }
  | { action: 'start'; comment?: string }
  | { action: 'stop'; comment?: string }

/** One mutation for every flow verb, so invalidation and error translation live in one place. */
export function useInitiativeFlowAction(id: number) {
  const invalidateWorkspace = useInvalidateInitiativeWorkspace()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (input: InitiativeFlowAction) => {
      const api = getApi().credit.initiatives[':id']
      const param = { id: String(id) }
      if (input.action === 'send_for_review') {
        return await (await api['send-for-review'].$post({ param })).json()
      }
      if (input.action === 'send_final') {
        return await (await api['send-final'].$post({ param })).json()
      }
      if (input.action === 'start') {
        return await (await api.start.$post({ param, json: { comment: input.comment } })).json()
      }
      if (input.action === 'stop') {
        return await (await api.stop.$post({ param, json: { comment: input.comment } })).json()
      }
      return await (
        await api.actions.$post({ param, json: { action: input.action, comment: input.comment } })
      ).json()
    },
    onSuccess: (result, input) => {
      invalidateWorkspace(id)
      if (input.action === 'advance' && 'requiredApproverCount' in result) {
        // A partial unanimous vote must not toast "approved" — the roster shows who is outstanding.
        const { approvedCount, requiredApproverCount, actionType } = result
        if (actionType === 'approved' && approvedCount < requiredApproverCount) {
          toast.success(
            t('initiatives.toasts.approvalRecorded', {
              approved: approvedCount,
              required: requiredApproverCount,
            }),
          )
          return
        }
      }
      toast.success(t(`initiatives.toasts.flow.${input.action}`))
    },
    onError,
  })
}

export function useImportInitiativeVersion(id: number) {
  const invalidateWorkspace = useInvalidateInitiativeWorkspace()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (file: File) => {
      const res = await getApi().credit.initiatives[':id'].versions.$post({
        param: { id: String(id) },
        form: { file },
      })
      return await res.json()
    },
    onSuccess: (result) => {
      invalidateWorkspace(id)
      toast.success(t('initiatives.toasts.versionImported', { number: result.versionNumber }))
    },
    onError,
  })
}

/** Live impact counts for the delete-version dialog — fetched only while it is open. */
export function useDeleteVersionImpact(id: number, versionNumber: number | null) {
  return useQuery({
    queryKey: ['initiativeVersionImpact', id, versionNumber ?? 0],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit.initiatives[':id'].versions[':versionNumber'][
        'delete-impact'
      ].$get(
        { param: { id: String(id), versionNumber: String(versionNumber) } },
        { init: { signal } },
      )
      return await res.json()
    },
    enabled: versionNumber !== null,
  })
}

export function useDeleteInitiativeVersion(id: number) {
  const invalidateWorkspace = useInvalidateInitiativeWorkspace()
  const { t } = useTranslation('credit')
  const onError = useInitiativeErrorToast()

  return useMutation({
    mutationFn: async (versionNumber: number) => {
      await getApi().credit.initiatives[':id'].versions[':versionNumber'].$delete({
        param: { id: String(id), versionNumber: String(versionNumber) },
      })
    },
    onSuccess: (_, versionNumber) => {
      invalidateWorkspace(id)
      toast.warning(t('initiatives.toasts.versionDeleted', { number: versionNumber }))
    },
    onError,
  })
}

/** Authenticated CSV download; the export is an audited action, so the timeline refreshes. */
export function useExportInitiativeForMarketing(id: number) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async () => {
      const { blob, filename } = await fetchCsvExport(`/credit/initiatives/${id}/export`)
      downloadFile(filename, blob)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] })
      toast.success(t('initiatives.toasts.exported'))
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('initiatives.toasts.error'))
    },
  })
}
